import axios, { AxiosRequestConfig, InternalAxiosRequestConfig } from 'axios'

const API_BASE = `${process.env.REACT_APP_API_URL || 'http://127.0.0.1:8000'}`

export const AXIOS_INSTANCE = axios.create({
  baseURL: API_BASE,
  withCredentials: true,
})

let refreshPromise: Promise<string | null> | null = null

const AUTH_SKIP_REFRESH = [
  '/api/auth/token',
  '/api/auth/refresh_token',
  '/api/auth/validate_token',
  '/api/auth/logout',
]

const isAuthSkipUrl = (url?: string): boolean =>
  Boolean(url && AUTH_SKIP_REFRESH.some((path) => url.includes(path)))

export const refreshAccessToken = (): Promise<string | null> => {
  if (refreshPromise) {
    return refreshPromise
  }

  refreshPromise = axios
    .post(
      `${API_BASE}/api/auth/refresh_token/`,
      {},
      {
        withCredentials: true,
        headers: { 'Content-Type': 'application/json' },
      },
    )
    .then((response) => {
      const access = response.data?.access as string | undefined
      if (!access) {
        return null
      }
      localStorage.setItem('token', access)
      AXIOS_INSTANCE.defaults.headers.common.Authorization = `Bearer ${access}`
      return access
    })
    .catch(() => null)
    .finally(() => {
      refreshPromise = null
    })

  return refreshPromise
}

export const clearAccessToken = (): void => {
  localStorage.removeItem('token')
  localStorage.removeItem('refresh_token')
  delete AXIOS_INSTANCE.defaults.headers.common.Authorization
}

export const logoutOnServer = (): Promise<void> =>
  axios
    .post(
      `${API_BASE}/api/auth/logout/`,
      {},
      {
        withCredentials: true,
        headers: { 'Content-Type': 'application/json' },
      },
    )
    .then(() => undefined)
    .catch(() => undefined)

let authRedirect: ((path: string) => void) | null = null
let onUnauthorized: (() => void) | null = null

export const setAuthRedirect = (handler: ((path: string) => void) | null): void => {
  authRedirect = handler
}

export const setOnUnauthorized = (handler: (() => void) | null): void => {
  onUnauthorized = handler
}

export const goToLogin = (path: string = '/login'): void => {
  if (authRedirect) {
    authRedirect(path)
    return
  }
  if (window.location.pathname !== path) {
    window.location.assign(path)
  }
}

// Add a request interceptor for authentication
AXIOS_INSTANCE.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token')

    if (token) {
      config.headers.Authorization = `Bearer ${token}`
    }

    return config
  },
  (error) => {
    return Promise.reject(error)
  },
)

AXIOS_INSTANCE.interceptors.response.use(
  (response) => {
    return response
  },
  async (error) => {
    const originalRequest = error.config as InternalAxiosRequestConfig & {
      _retry?: boolean
    }

    if (error.response?.status === 401 && !originalRequest._retry) {
      if (isAuthSkipUrl(originalRequest.url)) {
        return Promise.reject(error)
      }

      originalRequest._retry = true
      const newAccessToken = await refreshAccessToken()

      if (!newAccessToken) {
        if (onUnauthorized) {
          onUnauthorized()
        } else {
          clearAccessToken()
          await logoutOnServer()
          goToLogin()
        }
        return Promise.reject(error)
      }

      if (originalRequest.headers) {
        originalRequest.headers.Authorization = `Bearer ${newAccessToken}`
      }
      return AXIOS_INSTANCE(originalRequest)
    }

    return Promise.reject(error)
  },
)

export const customInstance = <T>(config: AxiosRequestConfig): Promise<T> => {
  const source = axios.CancelToken.source()

  const promise = AXIOS_INSTANCE({
    ...config,
    cancelToken: source.token,
  }).then(({ data }) => data)

  // @ts-ignore
  promise.cancel = () => {
    source.cancel('Query was cancelled by React Query')
  }

  return promise
}

export default customInstance
